// ============================================================
// CLUBHUB (aus dem Agent) – Der Mahnstand einer Rechnung
// ============================================================
// Uebernommen aus dem Agent (`lib/dunning/state.ts`). Abweichungen:
// Status „OPEN“ statt „SENT“; nach der letzten Stufe entscheidet im
// Verein der Vorstand (kein gerichtliches Mahnverfahren); die Staffel
// kommt aus `regeln.ts` (Einstellungen des Vereins). Dieselbe
// Funktion rechnet die Liste, das Blatt, den Mahnlauf und — noch
// einmal — die Server-Aktion (`dunningAllowed`).
// ------------------------------------------------------------
// Die eine Stelle, die sagt, WO eine offene Rechnung im
// Mahnwesen steht: welche Stufe erreicht ist, welche als
// Naechstes dran waere, ab wann sie dran ist, und was insgesamt
// hereinkommen muss.
//
// WARUM ES DIESE DATEI GIBT: vorher rechnete das Bauteil
// `verlauf.tsx` die naechste Stufe selbst — „hoechste vermerkte
// plus eins“. Das kannte keine Frist. Wer dreimal hintereinander
// klickte, hatte an einem Nachmittag Zahlungserinnerung, erste
// und zweite Mahnung verschickt; der Kunde bekam drei Briefe mit
// drei Fristen, von denen keine abgelaufen war. Eine Mahnstufe
// ist aber kein Knopfdruck, sondern ein Datum: sie wird faellig,
// wenn die Nachfrist der vorigen Stufe verstrichen ist.
//
// Die Funktion ist rein und bekommt `heute` von aussen. Der
// Mahnlauf rechnet eine ganze Liste auf einmal — faellt jede
// Zeile in ihr eigenes Heute, passt die Summe unten nicht mehr
// zu den Zeilen darueber. Und pruefen laesst sich Fristenlogik
// nur, wenn man das Datum stellen kann.
// ============================================================

import { interestCents, type DunningLevel, type DunningRules } from "./regeln";
import { formatDate } from "@/lib/utils";

/**
 * Wo die Rechnung im Mahnwesen steht.
 *
 * Die Schluessel sind zugleich die Zustaende, nach denen der
 * Mahnlauf filtert — `param` ist das Wort, das dabei in der
 * Adresse steht. Beides hier beieinander, weil eine Lage ohne
 * ihre Beschriftung im Kopf der Liste nicht auffindbar ist und
 * zwei getrennt gepflegte Listen frueher oder spaeter
 * auseinanderlaufen.
 */
export const DUNNING_STAGES = {
  due: {
    param: "faellig",
    label: "Fällig",
    hint: "Die nächste Mahnstufe ist dran",
  },
  running: {
    param: "frist",
    label: "Frist läuft",
    hint: "Gemahnt — die Nachfrist ist noch nicht abgelaufen",
  },
  grace: {
    param: "karenz",
    label: "Karenz",
    hint: "Überfällig, aber die Karenzfrist läuft noch",
  },
  paused: {
    param: "gestundet",
    label: "Gestundet",
    hint: "Mahnsperre gesetzt — bleibt aus dem Mahnlauf",
  },
  exhausted: {
    param: "ausgereizt",
    label: "Ausgereizt",
    hint: "Letzte Stufe verschickt und verstrichen — jetzt entscheidet der Mensch",
  },
} as const;

export type DunningStage = keyof typeof DUNNING_STAGES | "none";

/** Die Reihenfolge, in der die Lagen ueberall erscheinen — vom
 *  dringendsten zum ruhigsten. */
export const STAGE_ORDER = ["due", "running", "grace", "paused", "exhausted"] as const;

/** Eine bereits vermerkte Mahnung, so weit der Stand sie braucht. */
export type DunningEntry = {
  level: number;
  sentAt: Date;
  /** Die Frist, die auf dem Schreiben steht. Fehlt bei Eintraegen
   *  aus der Zeit vor der Spalte — dann wird sie nachgerechnet. */
  dueDate: Date | null;
  feeCents: number;
  emailedAt: Date | null;
};

/** Was der Stand von einer Rechnung wissen muss. */
export type DunningInvoice = {
  status: string;
  dueDate: Date;
  totalCents: number;
  paidCents: number;
  dunningPausedUntil: Date | null;
  dunnings: DunningEntry[];
};

export type DunningState = {
  stage: DunningStage;
  /** Die hoechste vermerkte Stufe; 0 heisst: noch nicht gemahnt. */
  level: number;
  /** Der Eintrag zu `level` — null, solange nicht gemahnt wurde. */
  last: DunningEntry | null;
  /** Die Stufe, die als Naechstes dran ist. Null nach der letzten:
   *  danach entscheidet im Verein der Vorstand. */
  next: DunningLevel | null;
  /** Ab wann `next` faellig ist. */
  nextDue: Date | null;
  /** Tage seit der Faelligkeit der Rechnung; negativ = noch hin. */
  overdueDays: number;
  /** Tage, bis `next` dran ist; negativ = ueberfaellig. */
  daysToNext: number | null;
  openCents: number;
  /** Summe der bereits vermerkten Mahngebuehren. */
  feeCents: number;
  /** Verzugszinsen auf den offenen Betrag, taggenau bis `heute`. */
  interestCents: number;
  /** Was insgesamt hereinkommen muss: offen plus Gebuehren plus Zinsen. */
  claimCents: number;
  pausedUntil: Date | null;
};

const TAG = 24 * 60 * 60 * 1000;

/**
 * Der Tag als Zahl.
 *
 * Wie `tageBis` in `lib/utils`: die Stichtage der Belege liegen
 * als `@db.Date` auf UTC-Mitternacht, gelesen werden sie mit den
 * Ortsgettern. Fuer eine Zeitzone oestlich von Greenwich faellt
 * das auf denselben Kalendertag — und nur der zaehlt hier.
 */
function tagZahl(datum: Date): number {
  return Date.UTC(datum.getFullYear(), datum.getMonth(), datum.getDate()) / TAG;
}

/** Ganze Tage von `von` bis `bis`; negativ, wenn `bis` frueher liegt. */
export function tageZwischen(von: Date, bis: Date): number {
  return tagZahl(bis) - tagZahl(von);
}

/** `datum` plus `tage`, auf demselben Kalendertag-Raster. */
export function plusTage(datum: Date, tage: number): Date {
  return new Date(new Date(datum).getTime() + tage * TAG);
}

/**
 * Der Kalendertag von `datum` als UTC-Mitternacht.
 *
 * So liegen die Datumsspalten der Belege (`@db.Date`) in der
 * Datenbank. Wer eine Frist aus `new Date()` speichert, ohne das
 * zu tun, schreibt in den fruehen Morgenstunden den Vortag hinein
 * — die Ortszeit ist dann schon einen Tag weiter als UTC.
 */
export function tagesbeginn(datum: Date): Date {
  return new Date(Date.UTC(datum.getFullYear(), datum.getMonth(), datum.getDate()));
}

/** Welche Status ueberhaupt gemahnt werden koennen. */
const MAHNBAR = new Set(["OPEN", "PARTIALLY_PAID", "OVERDUE"]);

/**
 * Der Mahnstand einer Rechnung.
 *
 * Gemahnt wird, was OFFEN ist, nicht der Rechnungsbetrag: nach
 * einer Teilzahlung faellt die Forderung, die Gebuehren der
 * bereits verschickten Schreiben bleiben.
 */
export function dunningState(
  invoice: DunningInvoice,
  rules: DunningRules,
  heute: Date = new Date(),
): DunningState {
  const offenCents = Math.max(0, invoice.totalCents - invoice.paidCents);
  const sortiert = [...invoice.dunnings].sort((a, b) => a.level - b.level);
  const last = sortiert.length > 0 ? sortiert[sortiert.length - 1] : null;
  const level = last?.level ?? 0;
  const feeCents = sortiert.reduce((summe, eintrag) => summe + eintrag.feeCents, 0);
  const next = rules.levels.find((stufe) => stufe.level === level + 1) ?? null;
  const overdueDays = tageZwischen(invoice.dueDate, heute);

  // Die Frist des letzten Schreibens: was daraufsteht, sonst
  // nachgerechnet. Nachgerechnet wird mit der Tageszahl DIESER
  // Stufe — nicht mit der der naechsten.
  const letzteFrist = last
    ? (last.dueDate ??
      plusTage(last.sentAt, rules.levels.find((s) => s.level === last.level)?.days ?? 7))
    : null;

  // Ab wann die naechste Stufe dran ist: vor der ersten Mahnung
  // die Faelligkeit plus Karenz, danach die Frist des letzten
  // Schreibens.
  const nextDue = next
    ? level === 0
      ? plusTage(invoice.dueDate, rules.graceDays)
      : letzteFrist
    : null;

  const zinsen = interestCents(offenCents, rules.interestPercent, invoice.dueDate, heute);

  const grund: Omit<DunningState, "stage"> = {
    level,
    last,
    next,
    nextDue,
    overdueDays,
    daysToNext: nextDue ? tageZwischen(heute, nextDue) : null,
    openCents: offenCents,
    feeCents,
    interestCents: zinsen,
    claimCents: offenCents + feeCents + zinsen,
    pausedUntil: invoice.dunningPausedUntil,
  };

  // Bezahlt, storniert, Entwurf, nichts mehr offen: die Rechnung
  // kommt im Mahnwesen nicht vor. Das ist ausdruecklich KEIN
  // eigener Zustand mit Zaehler — sonst stuende im Kopf der Liste
  // eine Zahl, auf die man klicken kann und dann nichts zu tun
  // findet.
  if (!MAHNBAR.has(invoice.status) || offenCents <= 0) {
    return { ...grund, stage: "none" };
  }

  // Noch gar nicht faellig: auch das gehoert nicht in den
  // Mahnlauf, egal was sonst am Vorgang haengt.
  if (overdueDays < 0 && level === 0) return { ...grund, stage: "none" };

  // Die Sperre schlaegt alles. Sie gilt bis EINSCHLIESSLICH ihres
  // Tages — wer „bis Freitag“ stundet, meint Freitag mit.
  if (invoice.dunningPausedUntil && tageZwischen(heute, invoice.dunningPausedUntil) >= 0) {
    return { ...grund, stage: "paused" };
  }

  // Nach der letzten Stufe gibt es keine weitere. Solange ihre
  // Frist laeuft, wird gewartet; danach ist der Agent am Ende
  // seines Wegs und sagt das auch.
  if (!next) {
    const verstrichen = letzteFrist ? tageZwischen(heute, letzteFrist) < 0 : true;
    return { ...grund, stage: verstrichen ? "exhausted" : "running" };
  }

  if (nextDue && tageZwischen(heute, nextDue) <= 0) return { ...grund, stage: "due" };

  return { ...grund, stage: level === 0 ? "grace" : "running" };
}

/**
 * Darf diese Stufe jetzt vermerkt werden?
 *
 * Der Mahnlauf und das Rechnungsblatt fragen dasselbe, und die
 * Server-Aktion fragt es noch einmal: zwischen dem Aufbau der
 * Liste und dem Klick liegt Zeit, in der eine Zahlung eingehen
 * oder ein Kollege dieselbe Stufe vermerken kann.
 */
export function dunningAllowed(
  state: DunningState,
  level: number,
): { ok: true } | { ok: false; grund: string } {
  if (state.stage === "none") {
    return { ok: false, grund: "Diese Rechnung ist nicht offen und überfällig." };
  }
  if (state.stage === "paused") {
    const bis = state.pausedUntil ? formatDate(state.pausedUntil) : undefined;
    return { ok: false, grund: `Für diese Rechnung gilt eine Mahnsperre bis ${bis}.` };
  }
  if (level <= state.level) return { ok: false, grund: "Diese Mahnstufe ist bereits vermerkt." };
  if (!state.next) {
    return {
      ok: false,
      grund: "Die letzte Mahnstufe ist verschickt. Über das Weitere entscheidet der Vorstand.",
    };
  }
  if (level !== state.next.level) {
    return { ok: false, grund: `Als Nächstes ist ${state.next.title} dran.` };
  }
  if (state.stage !== "due") {
    const ab = state.nextDue ? formatDate(state.nextDue) : undefined;
    return { ok: false, grund: `${state.next.title} ist erst ab dem ${ab} fällig.` };
  }
  return { ok: true };
}
